import { FC, useCallback, useState } from "react"
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ActionIcon, Button, Tooltip } from "rizzui";
import { Link } from "react-router-dom";
import { MdOutlineDownload, MdOutlineModeEditOutline } from "react-icons/md";
import { InvoiceService } from "../../../../services/invoices/invoiceService";
import useFilters from "../../../../hooks/useFilters";
import useHandleErrors from "../../../../hooks/useHandleErrors";
import useFetch from "../../../../hooks/useFetch";
import CustomTable from "../../../../components/table/CustomTable";
import Page from "../../../../layout/Page/Page";
import { FormDrawerView } from "../../../../components/forms/FormDrawer";
import { FaFileExcel } from "react-icons/fa";
import { IoDocumentTextSharp, IoTimeOutline } from "react-icons/io5";
import { FaCircleInfo } from "react-icons/fa6";
import { menuRoutes } from "../../../../router/menu";
import { HiOutlineCheckCircle, HiOutlineEye } from "react-icons/hi2";
import PaidPopover from "../../../../components/buttons/PaidPopover";
import CancelPopup from "../../../../components/buttons/CancelPopover";
import { getInvoiceVisual, invoiceStatuses } from "../../../invoices/list/InvoicesList";
import moment from "moment";
import { HiOutlineDownload } from "react-icons/hi";
import { ClientsApiResponse } from "../../../../type/entities/client-type";
import { usePrivilege } from "../../../../components/priviledge/PriviledgeProvider";
import InvoiceForm from "../../../invoices/components/InvoiceForm";
import cn from "../../../../utils/classNames";

interface HeadquarterProductProps {
}

const columnOrder = [
    'id',
    'serialCode',
    'company',
    'client',
    'clientCifNif',
    'address',
    'invoiceDate',
    'kgQuantity',
    'totalAmount',
    'ivaTax',
    'unitPrice',
    'isCancel',
    'cancelDate',
    'statusDate',
    'cancelReason',
    'isPaid',
    'paymentDate',
    'createdAt',
    'updatedAt'
]

interface ClientInvoicesProps {
    type?: 'proforma' | 'invoice';
}

const ClientInvoices: FC<ClientInvoicesProps> = ({ type = 'proforma' }) => {
    
    // STATES

    const [openFormDrawer, setOpenFormDrawer] = useState(false);
    const [selectedInvoice, setInvoiceSelected] = useState<any>(null);
    const [exporting, setExporting] = useState<boolean>(false);
    const service = new InvoiceService();

    const isProforma = type === 'proforma';
    const docName = isProforma ? 'proforma' : 'factura';
    const DocName = isProforma ? 'Proforma' : 'Factura';

    // HOOKS
    
    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE LAS FACTURAS DE UN CLIENTE
     * @EN GETS THE INVOICES OF A CLIENT
     * 
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const newFilters = {
            ...filters,
            filter_filters: {
                ...filters.filter_filters,
                client: id,
                is_paid: !isProforma
            }
        }
        const response = await service.listInvoices(newFilters);
        return response.getResponseData();
    }, [filters, isProforma]))
    // -------------------------------------------------------------------------------------------------------------

  //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la cancelación de una o multiples facturas
     * @EN Handles the cancellation of one or multiple invoices
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCancel = async (ids: string[], reason: string | undefined) => {
            try {
                const response = await (await service.cancelInvoice(ids, reason)).getResponseData();
                if (response.success) {
                    refetch();
                    toast.success('Factura/s cancelada/s correctamente');
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                handleErrors(error);
            }
        };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja el pago de una o multiples facturas
     *
     * @EN Handles the payment of one or multiple invoices
     */
    //-------------------------------------------------------------------------------------------------------------
    const handlePaid = async (ids: string[]) => {
            try {
                const response = await (await service.paidInvoice(ids)).getResponseData();
                if (response.success) {
                    refetch();
                    toast.success('Factura/s pagada/s correctamente');
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                handleErrors(error);
            }
        };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la eliminación de múltiples facturas
     * @EN Handles the deletion of multiple invoices
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {

            const response = await (await service.deleteMultiInvoices(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Factura eliminadas correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A EXCEL
     * @EN EXPORTS PICKUPS TO EXCEL
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleExportExcel = async () => {
        try {
            setExporting(true);
            const response = await service.exportInvoicesToExcel(filters);
            const fileData = response.getResponseData();
            const blob = new Blob([fileData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `facturas_clientes_${moment().format('DD-MM-YYYY_HH-mm-ss')}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Archivo Excel descargado con éxito');
        } catch (error: any) {
            toast.error(error.message || 'Error al exportar a Excel');
            setExporting(false);
        } finally {
            setExporting(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA RECOGIDAS A PDF
     * @EN EXPORTS PICKUPS TO PDF
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiExportPdf = async (selectedRows: any[]) => {
      try {
            // Extraer los IDs de las filas seleccionadas
            const ids = selectedRows.map(row => row.id);
            
            const response = await service.exportMultiInvoicesToPDF(ids);
          if (response && response.getResponseData()) {
              const fileData = response.getResponseData();
              const contentType = response.response?.headers?.['content-type'] || '';

              if (contentType.includes('application/pdf')) {
                  if (fileData) {
                      const blob = new Blob([fileData], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      
                      // Generar nombre personalizado para una sola factura
                      let fileName = `factura_cliente_${moment().format('DD-MM-YYYY_HH-mm-ss')}.pdf`;
                      
                      if (selectedRows.length === 1) {
                          const invoice = selectedRows[0];
                          const clientName = invoice.client?.companyName || 'Cliente';
                          const serialCode = invoice.serialCode || 'SIN_CODIGO';
                          
                          // Limpiar caracteres especiales del nombre
                          const cleanClientName = clientName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
                          const cleanSerialCode = serialCode.replace(/[^a-zA-Z0-9\-]/g, '');
                          
                          fileName = `${cleanClientName}_${cleanSerialCode}.pdf`;
                      }
                      
                      link.download = fileName;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      toast.success('Factura descargada con éxito');
                  }
              }
              else if (contentType.includes('application/zip')) {
                  if (fileData) {
                      const blob = new Blob([fileData], { type: 'application/zip' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                    
                      // Generar nombre del archivo ZIP con información de las facturas
                      const timestamp = moment().format('DD-MM-YYYY_HH-mm-ss');
                      let fileName = `${isProforma ? 'proformas' : 'facturas'}_clientes_${timestamp}.zip`;
                      
                      if (selectedRows.length > 1) {
                          // Para múltiples facturas, usar el número de facturas en el nombre
                          fileName = `${isProforma ? 'proformas' : 'facturas'}_${selectedRows.length}_clientes_${timestamp}.zip`;
                      }
                      
                      link.setAttribute('download', fileName);
                    
                      // Descargar el archivo
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    
                      toast.success(`${isProforma ? 'Proformas' : 'Facturas'} descargadas correctamente en formato ZIP`);
                  } else {
                      toast.error(`No se pudieron obtener los archivos de ${isProforma ? 'proformas' : 'facturas'}`);
                  }
              }
              else {
                  toast.error(`Error al descargar las ${isProforma ? 'proformas' : 'facturas'}`);
              }
          }
        } catch (error: any) {
            toast.error(`Error al descargar las ${isProforma ? 'proformas' : 'facturas'}: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const visual = getInvoiceVisual(row);
        const orderedRow: any = {};

        columnOrder.forEach((key) => {
            if (key === 'statusDate') {
                orderedRow.statusDate = visual.date; // guardamos raw
            } else {
                orderedRow[key] = row[key];
            }
        });

        // opcional: guarda el estado para el estilo de fila si tu CustomTable lo usa
        orderedRow.__rowState = visual.state;
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'client-invoices-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    divClassName='max-h-table-view'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    customRowsBackgroundColors={
                        invoiceStatuses.map((status) => ({
                            column: '__rowState',
                            values: [status.value],
                            gradientBg: status.gradientBg,
                            useGradient: true
                        }))
                    }
                    columnsNoSortables={[]}
                    columnsNotShown={['cancelDate','paymentDate','isCancel','isPaid', '__rowState', 'cancelReason', 'clientCifNif', 'address', 'company']}
                    overrideColumns={[
                        {
                            key: 'serialCode',
                            render: (row: any) => {
                                const original = row?.row?.original ?? row;
                                return (
                                    <span className="inline-flex items-center gap-2">
                                        <IoDocumentTextSharp className="h-4 w-4 shrink-0 text-gray-700" />
                                        <a
                                            href={`/invoices/${original.id}/profile`}
                                            className="font-bold hover:text-primary hover:underline"
                                        >
                                          {original.serialCode}
                                        </a>
                                    </span>
                                );
                            },
                        },
                        {
                            key: 'statusDate',
                            customHeader: 'Fecha de estado',
                            render: (row: any) => {
                                const original = row?.row?.original ?? row;
                                const { state, date } = getInvoiceVisual(original);
                                const label =
                                    state === 'cancelled' ? 'Cancelada' :
                                        state === 'paid' ? 'Pagada' : '—';

                                const dt = date ? new Date(date) : null;
                                const formatted = dt
                                    ? dt.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' })
                                    : '—';

                                return (
                                    <div className="flex flex-col">
                                        <span className="text-[11px] text-gray-500">{label}</span>
                                        <span>{formatted}</span>

                                        {state === 'cancelled' && date && (
                                            <Tooltip
                                                content={original?.cancelReason ? `${original.cancelReason}` : 'Sin motivo especificado'}
                                                placement="top"
                                                color="invert"
                                            >
                                                <span className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                                                  <FaCircleInfo className="h-3.5 w-3.5" />
                                                  Motivo
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'invoice', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={`Ver ${docName}`}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.invoices.path}/${row.id}/profile/${menuRoutes.invoices.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'invoice', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={`Editar ${docName}`}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setInvoiceSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Paid',
                            permissions: { group: 'invoice', permission: 'paid' },
                            hide: (row: any) => {
                                const original = row?.row?.original ?? row;
                                return Boolean(original?.isPaid) || Boolean(original?.isCancel);
                            },
                            render: (row: any) => {

                                return (
                                    <Tooltip size="sm" content={`Marcar ${docName} como pagada`} placement="top" color="invert">
                                        <div>
                                            <PaidPopover
                                                title={`Pagar ${docName}`}
                                                description={`¿Estás seguro de que deseas pagar la ${docName} ${row.serialCode}?`}
                                                onDelete={() => handlePaid([row.id])}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Cancel',
                            permissions: { group: 'invoice', permission: 'cancel' },
                            hide: (row: any) => {
                                // Accede de forma segura a la fila original
                                const original = row?.row?.original ?? row;
                                // Solo mostrar si NO está pagada y NO está cancelada
                                return Boolean(original?.isPaid) || Boolean(original?.isCancel);
                            },
                            render: (row: any) => {
                                return (
                                    <Tooltip size="sm" content={`Cancelar ${docName}`} placement="top" color="invert">
                                        <div>
                                            <CancelPopup
                                                title={`Cancelar ${docName}`}
                                                description={`¿Estás seguro de que deseas cancelar la ${docName} ${row.serialCode}?`}
                                                onDelete={(reason) => handleCancel([row.id], reason)}
                                                actionIconClassName="w-6 h-6"
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Export',
                            permissions: { group: 'invoice', permission: 'export' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={`Exportar ${docName}`}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                handleMultiExportPdf([row]);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineDownload className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        }, 
                        // {
                        //     label: 'Delete',
                        //     permissions: { group: 'invoice', permission: 'delete' },
                        //     render: (row: any) => {
                        //         return (
                        //             <Tooltip
                        //                 size="sm"
                        //                 content={'Eliminar factura'}
                        //                 placement="top"
                        //                 color="invert"
                        //             >
                        //                 <div>
                        //                     <DeletePopover
                        //                         title={`Eliminar factura`}
                        //                         description={`¿Estás seguro de que deseas eliminar la factura ${row.serialCode}?`}
                        //                         onDelete={() => handleDelete(row.id)}
                        //                     />
                        //                 </div>
                        //             </Tooltip>
                        //         );
                        //     },
                        // },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    multiActions={[
                        {
                            label: 'Descargar facturas seleccionadas',
                            permissions: {
                                group: 'invoice',
                                permission: 'export'
                            },
                            render: (ids: any[], selectedRows: any[], resetSelection: () => void) => {
                                return (
                                    <Tooltip
                                        size="md"
                                        content={'Exportar facturas seleccionadas'}
                                        placement="top"
                                    >
                                        <ActionIcon
                                            size="lg"
                                            color="primary"
                                            variant="outline"
                                            aria-label={'Exportar facturas'}
                                            className={cn(['cursor-pointer hover:!border-orange-600 hover:text-orange-600 border-orange-500 text-orange-500'])}
                                            onClick={() => {
                                                handleMultiExportPdf(selectedRows);
                                            }}
                                        >
                                            <HiOutlineDownload className="h-6 w-6" />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ClientsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                >
                    {userCan('export', 'invoice') && (
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={() => handleExportExcel()}
                            className="p-4 bg-green-600"
                        >
                            <FaFileExcel className="mr-2 min-h-[20px] min-w-[20px]" />
                            Exportar Excel
                        </Button>
                    )}
                    {userCan('create', 'invoice') &&
                        (
                            <Button
                                color="primary"
                                onClick={() => {
                                    setOpenFormDrawer(true)
                                    setInvoiceSelected(null)
                                }}
                                className={cn(['mt-4 md:mt-0 mx-2'])}
                            >
                                {isProforma ? "Emitir proforma" : "Emitir factura"}
                            </Button>
                        )
                    }
                </CustomTable>
            )}


            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    selectedInvoice ?
                        "Completa el formulario para editar los datos de la factura" :
                        "Completa el formulario para emitir una nueva factura"
                }
                drawerTitle={selectedInvoice ? `Editar ${docName} del cliente` : `Emitir ${docName} para el cliente`} submitText={
                    selectedInvoice ? `Guardar ${docName}` : `Emitir ${docName}`
                }>
                    <InvoiceForm setOpenModal={setOpenFormDrawer} refetch={refetch} clientId={id} invoiceSelected={selectedInvoice} isPaid={!isProforma} />
            </FormDrawerView>
            
        </Page>
    )
}

export default ClientInvoices;
